import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Search, MoreHorizontal, Settings, Users, X, Upload, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useS3Upload } from '../../hooks/useCommon';
import { useGetSubAdminsList, useUpdateSubAdmin, useUpdateSubAdminStatus, useAddSubAdmin, useDeleteSubAdmin } from "../../hooks/useSubAdmins"
import { useGetRoleList } from "../../hooks/useRoles"
import { useQueryClient } from '@tanstack/react-query';
import PhoneInput from 'react-phone-input-2';
import toast from 'react-hot-toast';

const SystemUsersManagement = () => {
    const [activeTab, setActiveTab] = useState({ name: 'Active', value: 1 });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [systemUsers, setSystemUsers] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [deleteReason, setDeleteReason] = useState('');
    const [openDropdown, setOpenDropdown] = useState(null);
    const queryClient = useQueryClient();
    const [identityCardImage, setIdentityCardImage] = useState(null);
    const createMutation = useAddSubAdmin()
    const deleteMutation = useDeleteSubAdmin()
    const updateMutation = useUpdateSubAdmin()
    const updateStatusMutation = useUpdateSubAdminStatus()
    const uploadMutation = useS3Upload();
    const dropdownRef = useRef(null);
    const filePath = "systemUsers"
    const { data: subAdminsListResponse, isSuccess: isSubAdminListSuccess } = useGetSubAdminsList(`?page=${currentPage}&limit=${rowsPerPage}&user_status=${activeTab?.value}&search_key=${searchTerm}`)
    const { data: rolesListResponse, isSuccess: isRolesListSuccess } = useGetRoleList()

    const UploadToS3 = async () => {
        if (identityCardImage) {
            let response = await uploadMutation.mutateAsync({
                file: identityCardImage,
                filePath
            })
            return response
        }
    }

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        country_code: '',
        phone_number: '',
        timezone_type: '',
        role_id: ''
    });

    const filteredUsers = systemUsers?.filter((user) => {
        const matchesTab = user?.status === activeTab?.value;
        return matchesTab;
    });

    const handleAddUser = async () => {
        setEditingUser(null);
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            country_code: '',
            phone_number: '',
            timezone_type: '',
            role_id: ''
        });
        setIdentityCardImage(null);
        setShowAddModal(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setFormData({
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
            country_code: user?.country_code,
            phone_number: user.phone_number,
            timezone_type: user?.timezone_type,
            role_id: user?.user_role_type,
            _id: user?._id
        });
        setShowAddModal(true);
    };

    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setDeleteReason('');
        setShowDeleteModal(true);
    };

    const handleFormSubmit = async () => {
        if (formData?.first_name == "") {
            toast.error("First name is not allowed to empty")
            return false
        }
        if (formData?.last_name == "") {
            toast.error("Last name is not allowed to empty")
            return false
        }
        if (formData?.email == "") {
            toast.error("Email is not allowed to empty")
            return false
        }
        if (formData?.country_code == "" || formData?.phone_number == "") {
            toast.error("Phone number is not allowed to empty")
            return false
        }
        if (formData?.timezone_type == "") {
            toast.error("Please Select TimeZone to Proceed.")
            return false
        }
        if (formData?.role_id == "") {
            toast.error("Please Select Role to Proceed.")
            return false
        }
        let dataObj = { ...formData }
        if (identityCardImage) {
            const imageResponse = await UploadToS3()
            if (!imageResponse) {
                toast.error('Please Select Identity Card to Proceed');
                return;
            }
            dataObj.identity_card = imageResponse?.Key
        }
        if (editingUser) {
            if (!("identity_card" in dataObj || dataObj.identity_card === "")) {
                dataObj.identity_card = editingUser?.identity_card
            }
            dataObj._id = editingUser?._id
            updateMutation.mutate(dataObj, {
                onSuccess: () => {
                    setShowAddModal(false);
                    queryClient.invalidateQueries({ queryKey: ['sub_admins'] });
                    toast.success("System user has been updated.")
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        } else {
            if (!("identity_card" in dataObj)) {
                toast.error('Please Select Identity Card to Proceed');
                return false
            }
            createMutation.mutate(dataObj, {
                onSuccess: () => {
                    setShowAddModal(false);
                    queryClient.invalidateQueries({ queryKey: ['sub_admins'] });
                    toast.success("New System user has been created.")
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const handleConfirmDelete = () => {
        if (userToDelete) {
            if (deleteReason == "") {
                toast.error('Please enter the reason for deletion');
                return false
            }
            deleteMutation.mutate(userToDelete?._id, {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['sub_admins'] });
                    toast.success("User has been deleted.")
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                    setDeleteReason('');
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const handleSuspendUser = (user, status) => {
        setOpenDropdown(null);
        updateStatusMutation.mutate({
            status,
            _id: user?._id,
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['sub_admins'] });
                toast.success("User status has been updated.")
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const toggleDropdown = (userId) => {
        setOpenDropdown(openDropdown === userId ? null : userId);
    };

    const handleDropdownAction = (action, user) => {
        setOpenDropdown(null);
        switch (action) {
            case 'edit':
                handleEditUser(user);
                break;
            case 'suspend':
                handleSuspendUser(user, 3);
                break;
            case 'activate':
                handleSuspendUser(user, 1);
                break;
            case 'delete':
                handleDeleteUser(user);
                break;
            default:
                break;
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setIdentityCardImage(file);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePhoneChange = (value, data) => {
        setFormData(prev => ({ ...prev, phone_number: value?.slice(data?.dialCode?.length) }));
        setFormData(prev => ({ ...prev, country_code: data.dialCode }));
    }

    useEffect(() => {
        if (isSubAdminListSuccess) {
            setSystemUsers(subAdminsListResponse?.data?.data?.result)
            let tp = Math.ceil((subAdminsListResponse?.data?.data?.totalCount) / rowsPerPage)
            setTotalPages(tp == 0 ? 1 : tp)
        }
    }, [subAdminsListResponse, isSubAdminListSuccess])

    useEffect(() => {
        if (isRolesListSuccess) {
            setRolesList(rolesListResponse?.data?.data?.result)
        }
    }, [rolesListResponse, isRolesListSuccess])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="bg-white border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-900">System Users</span>
                    </div>
                </div>
                {/* Page Content */}
                <div className="flex-1 px-1 py-4">
                    <div className="max-w-7xl">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">System Users</h1>
                                <p className="text-gray-600">All the system users are listed here.</p>
                            </div>
                            <button
                                onClick={handleAddUser}
                                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium">
                                Add New System User
                            </button>
                        </div>
                        {/* Tabs */}
                        <div className="flex space-x-8 mb-6">
                            {[{ name: 'Active', value: 1 }, { name: 'Inactive', value: 3 }].map((tab) => (
                                <button
                                    key={tab?.value}
                                    onClick={() => setActiveTab(tab)}
                                    className={`pb-2 border-b-2 font-medium transition-colors ${activeTab?.value === tab?.value ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                                    {tab?.name}
                                </button>
                            ))}
                        </div>
                        {/* Search */}
                        <div className="mb-6">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search keyword"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        {/* Table */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            S.No
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Full Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Phone Number
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredUsers.map((user, index) => (
                                        <tr key={user?._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{`${user?.first_name} ${user?.last_name}`}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{user?.privilege_roles?.role}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{user?.privilege_roles?.role_type}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{user?.email}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{`${user?.country_code}${user?.phone_number}`}</td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                <div className="relative">
                                                    <button
                                                        onClick={() => toggleDropdown(user?._id)}
                                                        className="hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                                                        <MoreHorizontal className="w-5 h-5" />
                                                    </button>
                                                    {/* Dropdown Menu */}
                                                    {openDropdown === user?._id && (
                                                        <div className="absolute right-0 top-8 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleDropdownAction('edit', user)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                                                                    <Settings className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                {activeTab.value == 1 ?
                                                                    <button
                                                                        onClick={() => handleDropdownAction('suspend', user)}
                                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                                                                        <Users className="w-4 h-4" />
                                                                        <span>Suspend</span>
                                                                    </button>
                                                                    : <button
                                                                        onClick={() => handleDropdownAction('activate', user)}
                                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                                                                        <Users className="w-4 h-4" />
                                                                        <span>Activate</span>
                                                                    </button>
                                                                }
                                                                <button
                                                                    onClick={() => handleDropdownAction('delete', user)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2">
                                                                    <X className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination */}
                            <div className="flex justify-end m-4">
                                <div className="flex items-center">
                                    <div className="mr-4 flex justify-between">
                                        <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                                        <select
                                            className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                            value={rowsPerPage}
                                            onChange={(e) => { setCurrentPage(1); setRowsPerPage(Number(e.target.value)) }}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="mr-4">Page {currentPage} of {totalPages}</span>
                                        <div className="flex">
                                            <button
                                                className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}>
                                                <ChevronsLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}>
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}>
                                                <ChevronRight size={16} />
                                            </button>
                                            <button
                                                className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}>
                                                <ChevronsRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add/Edit User Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {editingUser ? 'Edit System User' : 'Add System User'}
                                    </h2>
                                </div>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First name*
                                    </label>
                                    <input
                                        type="text"
                                        value={formData?.first_name}
                                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                                        placeholder="First name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                {/* Last Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData?.last_name}
                                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                                        placeholder="Last name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email*
                                    </label>
                                    <input
                                        type="email"
                                        value={formData?.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Email"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                {/* Phone Number */}
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium mb-1 text-gray-700`}>
                                        Contact number*
                                    </label>
                                    <div className="flex">
                                        <PhoneInput
                                            country={'us'}
                                            value={formData?.country_code + formData?.phone_number}
                                            onChange={handlePhoneChange}
                                            inputStyle={{
                                                width: '100%',
                                                backgroundColor: '#fff',
                                                color: '#000',
                                                borderColor: '#D1D5DB',
                                                borderRadius: '0.375rem',
                                                padding: '22px 50px',
                                                fontSize: '1rem',
                                            }}
                                            buttonStyle={{
                                                backgroundColor: '#f3f4f6',
                                                borderColor: '#D1D5DB',
                                            }}
                                            containerStyle={{
                                                width: '100%',
                                                height: '45px'
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* Timezone */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Timezone*
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData?.timezone_type}
                                            onChange={(e) => handleInputChange('timezone_type', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
                                            <option value="">Select</option>
                                            <option value="UTC">UTC</option>
                                            <option value="EST">EST</option>
                                            <option value="PST">PST</option>
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                {/* Role id */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Role name*
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData?.role_id}
                                            onChange={(e) => handleInputChange('role_id', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
                                            <option value="">Select</option>
                                            {rolesList && rolesList?.map((rl) => (
                                                <option value={rl?._id}>{rl?.role}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                {/* Identity Card */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Identity Card*
                                    </label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-md p-4">
                                        <input
                                            type="file"
                                            id="identity-card"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            accept="image/*,.pdf"
                                        />
                                        <label
                                            htmlFor="identity-card"
                                            className="cursor-pointer flex items-center space-x-2 text-gray-600">
                                            <Upload className="w-5 h-5" />
                                            <span className="text-sm">
                                                {identityCardImage?.name ?? 'Choose file No file chosen'}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {/* Modal Footer */}
                            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFormSubmit}
                                    className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            Are you absolutely sure?
                        </h2>
                        <p className="text-sm text-gray-600 mb-4">
                            {`This will permanently delete the user ${userToDelete?.first_name} ${userToDelete?.last_name} from Newboo Admin.`}
                        </p>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason*
                            </label>
                            <textarea
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                placeholder="Enter"
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            />
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default SystemUsersManagement;